import { ConfirmDialog } from 'components/Dialog/ConfirmDialog/ConfirmDialog';
import { useAuthDispatch } from 'contexts/auth/useAuth';
import { useMutateInspection } from 'hooks/Inspections/InspectionsHooks';
import lgFullScreen from 'lightgallery/plugins/fullscreen';
import lgHash from 'lightgallery/plugins/hash';
import lgPager from 'lightgallery/plugins/pager';
import lgRotate from 'lightgallery/plugins/rotate';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import LightGallery from 'lightgallery/react';
import { IPhoto } from 'pages/SchedulerList/components/Inspection/Inspection.types';
import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import PhotoService from 'services/PhotoService/photoService';
import { getErrorMessage, hasException } from 'utils/exceptions/formatDeleteFileException';
import PhotoEditor, { IPhotoEditorRef } from '../PhotoEditor/PhotoEditor';
import './PhotoGallery.css';
import { Container } from './PhotoGallery.styles';
import { IPhotoGalleryProps } from './PhotoGallery.types';
import { GalleryImage } from './components/GalleryImage';
import { useLightGallery } from './hooks/useLightGallery';

const PhotoGallery: React.FC<IPhotoGalleryProps> = ({ inspection }) => {
  const editorRef = useRef<IPhotoEditorRef>(null);
  const { isOperator } = useAuthDispatch();

  const [selectedPhoto, setSelectedPhoto] = useState<IPhoto>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { invalidateInspectionParallel } = useMutateInspection();

  const { lightGallery, onInit, onBeforeOpen, onAfterClose, handleImageContainerClick, dynamicEl } = useLightGallery({
    photos: inspection.photos,
    onEditClick: () => {
      editorRef.current?.openImageEditor();
    },
  });

  const handlePhotoDeletion = (event: React.MouseEvent<HTMLElement>, photo: IPhoto) => {
    event.stopPropagation();
    setSelectedPhoto(photo);
    setShowConfirmDialog(true);
  };

  const getCurrentPhoto = useCallback(() => {
    if (!inspection.photos) return undefined;
    const fileIndex = lightGallery.current?.index || 0;
    return inspection.photos[fileIndex];
  }, [inspection.photos, lightGallery]);

  const closeGallery = useCallback(() => {
    lightGallery.current?.closeGallery();
  }, [lightGallery]);

  const canDeletePhoto = !isOperator();

  const deletePhoto = async () => {
    if (!selectedPhoto?.id || !selectedPhoto.inspectionId) return;
    PhotoService.deletePhoto(selectedPhoto)
      .then(() => {
        if (selectedPhoto.inspectionId) {
          invalidateInspectionParallel(selectedPhoto.inspectionId);
        }
        toast.success('Foto excluída com sucesso!');
      })
      .catch((error) => {
        const exception = error.response?.data?.exception;
        if (exception && hasException(exception)) {
          toast.warning(getErrorMessage(exception));
        } else {
          toast.error('Erro ao excluir foto!');
        }
      });
  };

  return (
    <>
      <Container>
        {inspection.photos?.map((photo, index) => (
          <GalleryImage
            key={photo.id}
            photo={photo}
            index={index}
            onImageClick={handleImageContainerClick}
            onDeleteClick={handlePhotoDeletion}
            canDelete={canDeletePhoto}
          />
        ))}
      </Container>

      <LightGallery
        licenseKey={process.env.REACT_APP_LIGHTGALLERY_LICENSE_KEY}
        onInit={onInit}
        onBeforeOpen={onBeforeOpen}
        onAfterClose={onAfterClose}
        dynamic
        showZoomInOutIcons
        actualSize={false}
        download={false}
        plugins={[lgThumbnail, lgZoom, lgFullScreen, lgHash, lgPager, lgRotate]}
        dynamicEl={dynamicEl}
      />

      <PhotoEditor
        ref={editorRef}
        inspection={inspection}
        getCurrentPhoto={getCurrentPhoto}
        closeGallery={closeGallery}
      />

      {showConfirmDialog && (
        <ConfirmDialog
          dialogVisibility={showConfirmDialog}
          handleClose={() => setShowConfirmDialog(false)}
          testID="confirm-dialog"
          onSubmit={deletePhoto}
          title="Deseja realmente excluir a foto?"
          subtitle="Ao confirmar, essa ação não poderá ser desfeita."
        />
      )}
    </>
  );
};

export default PhotoGallery;
